import TextArea from 'components/Common/Form/Input/TextArea'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Expander from 'components/utils/Expander'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import type { ChangeEventHandler } from 'react'
import { useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import LuxPlusManageAccountModalMembershipStatusBanner from './LuxPlusManageAccountModalMembershipStatusBanner'
import LuxPlusManageAccountModalSavingsBanner from './LuxPlusManageAccountModalSavingsBanner'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import {
  RENEWAL_CANCELLATION_REASONS,
  useRenewalCancellationReasonLabels,
  useRenewalCancellationSubmit,
} from './renewalCancellationReasons'

interface Props {
  item: App.MemberSubscriptionItem
  onRenewCancelSuccess: () => void
  onBack: () => void
}

function LuxPlusManageAccountModalRenewalCancellation({
  item,
  onRenewCancelSuccess,
  onBack,
}: Props) {
  const intl = useIntl()

  const reasonLabels = useRenewalCancellationReasonLabels()
  const onConfirmUpdateRenewalStatus = useRenewalCancellationSubmit(onRenewCancelSuccess)

  const [selectedReason, setReason] = useState<string | undefined>()
  const handleReasonChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    setReason(event.currentTarget.value)
  }, [])

  const fetching = useAppSelector(state => state.auth.account.luxPlus.member.subscription.fetching)

  const modalElement = useModalElementContext()
  const dismissModal = useCallback(() => {
    modalElement.resolve()
  }, [modalElement])

  const selectedOtherReason = !!selectedReason?.startsWith('Other')

  return <form onSubmit={onConfirmUpdateRenewalStatus}>
    <ModalHeader
      title={intl.formatMessage({
        id: '5WbkpEPsNFkjbtbCUlu2',
        defaultMessage: 'Cancel Renewal',
        description: 'Title for the cancel renewal modal in the cancellation or refund flow',
      })}
      onBackButtonClick={onBack}
    />
    <ModalBody>
      <ModalContent>
        <LoadingIndicator floating visible={fetching} />
        <Group direction="vertical" gap={32}>
          <Group direction="vertical" gap={12}>
            <Heading variant="heading4">
              {intl.formatMessage(
                {
                  id: 'Ysua2sfeWS6JPnNR9Pjd',
                  defaultMessage: 'Thank you for being a {programName} member and we’re sorry to see you go.',
                  description: 'Heading thanking the user for their membership when cancelling renewal',
                },
                { programName: LUXURY_PLUS.PROGRAM_NAME },
              )}
            </Heading>
            <BodyText variant="large">
              {intl.formatMessage(
                {
                  id: 'FQ6NQm5lMSXFzjhbOwkq',
                  defaultMessage: 'You’ll have access to your {programName} benefits until the end of your current membership year.',
                  description: 'Body text informing user they keep benefits until end of membership year',
                },
                { programName: LUXURY_PLUS.PROGRAM_NAME },
              )}
            </BodyText>
          </Group>
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={16}>
              <BodyText variant="medium" weight="bold">{intl.formatMessage({
                id: '9TnkGyET5PVqIH-WiU0c',
                defaultMessage: 'Main reason for cancelling',
                description: 'Label for the cancellation reason selection in the cancel renewal modal',
              })}
              </BodyText>
              <Group direction="vertical" gap={8}>
                {RENEWAL_CANCELLATION_REASONS.map((reason) => <RadioInput
                  key={reason}
                  name="reason"
                  value={reason}
                  size="medium"
                  required
                  onChange={handleReasonChange}
                >
                  {reasonLabels[reason]}
                </RadioInput>)}
              </Group>
              <Expander expanded={selectedOtherReason}>
                <TextArea
                  name="comment"
                  required={selectedOtherReason}
                  placeholder={intl.formatMessage({
                    id: '3cHjt-82CnGXW7PfgQym',
                    defaultMessage: 'Please specify',
                    description: 'Placeholder text for the free-text cancellation reason input',
                  })}
                />
              </Expander>
            </Group>
            <Group direction="vertical" gap={8} noDisplayWhenEmpty>
              <LuxPlusManageAccountModalSavingsBanner />
              <LuxPlusManageAccountModalMembershipStatusBanner item={item} />
            </Group>
          </Group>
        </Group>
      </ModalContent>
    </ModalBody>
    <ModalFooter
      primaryActionProps={{
        children: intl.formatMessage({
          id: 'zTgaREuhmqfKir1svtAK',
          defaultMessage: 'Keep my membership',
          description: 'Primary action button to keep membership and dismiss cancellation',
        }),
        onClick: dismissModal,
      }}
      secondaryActionProps={{
        children: intl.formatMessage({
          id: 'ren3ZISQVRnCJt3l-gBM',
          defaultMessage: 'Cancel renewal',
          description: 'Secondary action button to confirm cancellation of renewal',
        }),
        type: 'submit',
      }}
    />
  </form>
}

export default LuxPlusManageAccountModalRenewalCancellation
